import React, { useState } from 'react';
import ServiceLayout from '../components/ServiceLayout.jsx';
import { Quote, Plus, X, Phone, Calendar, ArrowRight, ArrowDown, ArrowUp, RefreshCw, ExternalLink, Check, AlertCircle, Info, Wind, Unlock, Clock, Footprints, Brain, Zap, EyeOff, Lock, CloudRain, BatteryWarning, Leaf, Activity, Search, Smile, Shield } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const AnxietyAccordion = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
      {items.map((item, index) => (
        <div key={item.title} className="border-b border-[#E8DDCE]">
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full py-4 sm:py-6 flex justify-between items-center text-left group cursor-pointer"
          >
            <h3 className="text-xl md:text-2xl font-serif text-[#3B352F] group-hover:text-[#123F13] transition-colors">
              {item.title}
            </h3>
            <div className="text-[#123F13] transition-transform duration-300">
              {openIndex === index ? <X size={24} /> : <Plus size={24} />}
            </div>
          </button>
          <AnimatePresence>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="pb-8 pr-4">
                  <p className="text-[#3B352F]/70 leading-relaxed text-base md:text-lg">
                    {item.content}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const FAQAccordion = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <div key={index} className="bg-white rounded-xl border border-[#E8DDCE] overflow-hidden shadow-sm">
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full p-4 sm:p-6 flex justify-between items-center text-left group cursor-pointer"
          >
            <h3 className="text-lg font-bold text-[#123F13]">
              {item.question}
            </h3>
            <div className="text-[#8C2F12] transition-transform duration-300">
              {openIndex === index ? <X size={20} /> : <Plus size={20} />}
            </div>
          </button>
          <AnimatePresence>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <div className="px-4 pb-4 sm:px-6 sm:pb-6 text-[#3B352F]/70 leading-relaxed">
                  {item.answer}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const Angst = () => {
  const canonicalUrl = "https://corehypnose.dk/angst";
  
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Angstbehandling med Hypnose og Metakognitiv Terapi",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Core Hypnose v/ Lars Nissen Corell – Psykoterapeut MPF",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Valby Langgade 142",
        "addressLocality": "Valby",
        "postalCode": "2500",
        "addressCountry": "DK"
      },
      "telephone": "+4527204671"
    },
    "description": "Professionel angstbehandling i Valby og online. Slip fri af uro, hjertebanken og bekymringer med Lars Nissen Corell – Psykoterapeut MPF. Over 26 års erfaring.",
    "areaServed": "Danmark",
    "serviceType": "Psykoterapi"
  };

  return (
    <ServiceLayout 
      title="Angstbehandling - Slip fri af uro og bekymringer"
      subtitle="Hypnose og metakognitiv terapi hjælper dig med at slippe angsten og få ro i kroppen og tankerne – så du kan leve dit liv med mere frihed og energi."
      image="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/3268f6ee-7f83-42ca-a08f-849a8999b741.png"
      description="Søger du en tryg og personlig tilgang til angstbehandling? Hos Core Hypnose møder du mig, en erfaren terapeut, som hjælper dig med at slippe angsten gennem hypnose og metakognitiv terapi."
      actions={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Link to="/bestil-tid" className="inline-flex items-center justify-center gap-2 bg-[#193f10] text-white px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#193f10]/90 transition-all shadow-md w-full sm:w-auto">
            <Calendar size={18} /> Book første behandlingssession
          </Link>
          <a href="tel:27204671" className="inline-flex items-center justify-center gap-2 border-2 border-[#193f10] text-[#193f10] px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#faf7f2] transition-all w-full sm:w-auto">
            <Phone size={18} /> Tlf. 27 20 46 71
          </a>
        </div>
      }
    >
      <Helmet>
        <title>5 grunde til effektiv angstbehandling hos Core Hypnose</title>
        <meta name="description" content="Få tryg og personlig angstbehandling med Core Hypnose og Metakognitiv terapi. Jeg hjælper dig med at slippe angsten og finde ro. Book en intro session i dag" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="5 grunde til effektiv angstbehandling hos Core Hypnose" />
        <meta property="og:description" content="Få tryg og personlig angstbehandling med Core Hypnose og Metakognitiv terapi. Jeg hjælper dig med at slippe angsten og finde ro. Book en intro session i dag" />
        <meta property="og:image" content="" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="" />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* Testimonial Section - MOVED HERE */}
      <div className="bg-[#123F13] text-white p-6 sm:p-10 md:p-12 rounded-2xl relative overflow-hidden mb-16">
        <Quote className="absolute -top-4 -right-4 sm:top-8 sm:right-8 text-white/10 w-24 h-24 sm:w-32 sm:h-32" />
        <div className="relative z-10 max-w-2xl">
          <h3 className="text-2xl font-serif mb-6 italic">“Jeg kunne endelig trække vejret helt ned i maven”</h3>
          <p className="text-white/80 mb-6 leading-relaxed">
            Mette kom med vedvarende uro og tankemylder. Hun havde prøvet flere ting før, men følte sig fanget i sine bekymringer.
            Efter første session sagde hun: “Det var første gang i lang tid, jeg mærkede ro i kroppen, som om skuldrene sænkede sig automatisk.”
            I takt med at hun lærte de metakognitive teknikker og oplevede roen i hypnosen, begyndte angsten at fylde mindre, og energi og frihed vendte tilbage.
          </p>
          <p className="font-bold">— Mette, 42 år</p>
          <p className="text-white/60 text-sm mt-4 italic">Mette oplevede ro allerede efter første session – og det kan du også.</p>
        </div>
      </div>

      {/* Hvad er angst sektion */}
      <section className="mb-16">
        <div className="w-full">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">Hvad er angst – og hvordan føles det?</h2>
          <div className="space-y-4 text-[#3B352F]/70 leading-relaxed text-lg">
            <p>
              Angst er en naturlig reaktion i kroppen, men for mange tager den overhånd. Tankerne kredser, kroppen spænder, og hverdagen føles pludselig overvældende. Det er som om kroppen reagerer på fare, selv når der ikke er nogen reel trussel til stede.
            </p>
            <p>
              Angst kan vise sig som uro, hjertebanken, undgåelsesadfærd, bekymringer eller panikfølelse. Mange føler, de ikke længere har kontrol over deres reaktioner eller tanker. For nogle opleves det som et konstant alarmberedskab, hvor de mindste ting kan trigge voldsomme reaktioner.
            </p>
            <p>
              Man kan miste lysten til sociale sammenhænge, sove dårligt og have svært ved at fokusere. Over tid bliver angsten selvforstærkende, jo mere man forsøger at undgå den, jo mere fylder den.
            </p>
            <p className="font-medium text-[#123F13]">
              Den gode nyhed er, at angst ikke behøver at styre dit liv. Med den rette angstbehandling kan du lære at forstå og håndtere dine reaktioner, så du igen får frihed og ro i kroppen.
            </p>
          </div>

          {/* Angst eller almindelig bekymring? Infografisk sektion */}
          <div className="mt-16 bg-[#FAF7F2] rounded-2xl border border-[#E8DDCE] p-6 md:p-10 shadow-sm">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <h3 className="text-2xl md:text-3xl font-serif text-[#123F13] mb-4">
                Angst eller almindelig bekymring?
              </h3>
              <p className="text-[#3B352F]/80 text-base md:text-lg leading-relaxed">
                Det handler ikke om at diagnosticere dig selv. Det handler om at forstå, hvornår bekymring er almindelig — og hvornår angst begynder at begrænse din frihed.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Kolonne 1: Almindelig bekymring */}
              <div className="bg-white rounded-xl border border-[#E8DDCE] p-6 sm:p-8 flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-300">
                <div>
                  <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start text-center sm:text-left mb-6 gap-4">
                    <div className="flex flex-col items-center sm:items-start">
                      <span className="inline-block bg-[#E2ECE3] text-[#123F13] text-xs font-semibold px-3 py-1 rounded-full mb-2">
                        Ofte en naturlig reaktion
                      </span>
                      <h4 className="text-2xl font-serif font-bold text-[#123F13]">Almindelig bekymring</h4>
                    </div>
                    {/* Minimalistisk stregtegning af en rolig plante/blad der svajer i vinden */}
                    <div className="bg-[#E2ECE3]/40 p-3 rounded-full shrink-0 text-[#123F13] self-center sm:self-start">
                      <svg aria-hidden="true" viewBox="0 0 100 100" className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M50 85 C50 55, 60 35, 75 20" />
                        <path d="M53 65 C65 60, 70 45, 60 40 C50 35, 48 50, 53 65 Z" fill="#E2ECE3" fillOpacity="0.4" />
                        <path d="M47 45 C35 40, 30 25, 40 20 C50 15, 52 30, 47 45 Z" fill="#E2ECE3" fillOpacity="0.2" />
                        <path d="M20 35 Q30 30 40 35" strokeDasharray="3 3" />
                        <path d="M15 55 Q25 50 35 55" strokeDasharray="3 3" />
                      </svg>
                    </div>
                  </div>
                  
                  <p className="text-[#3B352F]/70 text-base mb-6 leading-relaxed">
                    Bekymringer opstår i perioder, men de styrer ikke hele din hverdag.
                  </p>
                  
                  <ul className="space-y-3 pl-2">
                    {[
                      "Kommer og går",
                      "Du kan slippe den igen",
                      "Fylder ikke hele dagen",
                      "Stopper dig ikke væsentligt",
                      "Du kan stadig træffe beslutninger"
                    ].map((text, index) => {
                      return (
                        <li key={index} className="flex items-start gap-3 text-[#3B352F]/80">
                          <span className="w-2 h-2 rounded-full bg-[#123F13] mt-2 shrink-0" />
                          <span className="text-sm md:text-base font-medium text-[#3B352F]">{text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* Kolonne 2: Angst */}
              <div className="bg-white rounded-xl border border-[#E8DDCE] p-6 sm:p-8 flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-[#123F13]/3 rounded-full -mr-8 -mt-8 pointer-events-none" />
                <div>
                  <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start text-center sm:text-left mb-6 gap-4">
                    <div className="flex flex-col items-center sm:items-start">
                      <span className="inline-block bg-[#F4EFE7] text-[#123F13] text-xs font-semibold px-3 py-1 rounded-full mb-2">
                        Relevant at tage alvorligt
                      </span>
                      <h4 className="text-2xl font-serif font-bold text-[#123F13]">Angst</h4>
                    </div>
                    {/* Minimalistisk stregtegning af et uroligt sind / stormsky med lyn */}
                    <div className="bg-[#F4EFE7] p-3 rounded-full shrink-0 text-[#123F13] self-center sm:self-start">
                      <svg aria-hidden="true" viewBox="0 0 100 100" className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M35 55 C25 55, 20 45, 25 35 C20 25, 30 15, 42 20 C48 10, 65 12, 70 22 C78 20, 85 28, 82 38 C87 45, 82 55, 72 55 Z" fill="#F4EFE7" fillOpacity="0.4" />
                        <path d="M50 45 L42 62 L52 62 L45 82 L62 58 L52 58 Z" fill="#F4EFE7" fillOpacity="0.8" />
                        <path d="M25 70 Q30 65 35 75" strokeDasharray="2 2" />
                        <path d="M65 75 Q70 70 75 80" strokeDasharray="2 2" />
                      </svg>
                    </div>
                  </div>

                  <p className="text-[#3B352F]/70 text-base mb-6 leading-relaxed">
                    Angst føles ikke kun som bekymring. Den sætter ofte både krop og sind i alarm.
                  </p>

                  <ul className="space-y-3 pl-2">
                    {[
                      "Sætter kroppen i alarm",
                      "Skaber undgåelse",
                      "Gør hverdagen mindre fri",
                      "Fylder meget mentalt og fysisk",
                      "Du bruger meget energi på at kontrollere tanker, krop eller situationer"
                    ].map((text, index) => {
                      return (
                        <li key={index} className="flex items-start gap-3 text-[#3B352F]/80">
                          <span className="w-2 h-2 rounded-full bg-[#123F13] mt-2 shrink-0" />
                          <span className="text-sm md:text-base font-semibold text-[#3B352F]">{text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>

            {/* Bundnote */}
            <div className="mt-8 bg-white/60 border border-[#E8DDCE] rounded-xl p-5 flex gap-4 items-start">
              <Leaf className="text-[#123F13] shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-serif text-[#123F13] text-lg font-semibold mb-1">En vigtig pointe</h4>
                <p className="text-sm md:text-base text-[#3B352F]/80 leading-relaxed">
                  Du behøver ikke have det “slemt nok” for at søge hjælp. Hvis bekymringer eller angst begynder at begrænse din hverdag, kan det være hjælpsomt at tale med nogen.
                </p>
              </div>
            </div>
          </div>

          {/* Different types of anxiety accordion - MOVED UP */}
          <div className="py-20 border-t border-[#E8DDCE]/60 mt-20">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="text-[#8C2F12] text-xs font-semibold tracking-[0.15em] uppercase mb-4 block">Overblik</span>
              <h2 className="text-4xl md:text-5xl font-serif text-[#123F13] mb-6">Forskellige former for angst</h2>
              <p className="text-[#3B352F]/70 text-lg">
                Angst kan komme til udtryk på mange måder. Herunder kan du læse mere om de mest almindelige former, jeg arbejder med i klinikken.
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <AnxietyAccordion items={[
                {
                  title: "Panikangst",
                  content: (
                    <>
                      Panikangst viser sig som pludselige og intense anfald af frygt med voldsomme fysiske symptomer som hjertebanken, åndenød og en følelse af at miste kontrollen eller dø. Det kan føles ekstremt skræmmende, men er ikke farligt.{" "}
                      <Link to="/panikangst" className="text-[#8C2F12] font-semibold hover:underline inline-flex items-center gap-1 text-sm mt-2 block">
                        Læs mere om panikangst her <ArrowRight size={14} />
                      </Link>
                    </>
                  )
                },
                {
                  title: "Generaliseret angst",
                  content: (
                    <>
                      Ved generaliseret angst oplever man en vedvarende og overdreven bekymring omkring mange forskellige ting i hverdagen – alt fra økonomi og sundhed til familiens trivsel. Man føler sig ofte i et konstant alarmberedskab med indre uro.{" "}
                      <Link to="/generaliseret-angst" className="text-[#8C2F12] font-semibold hover:underline inline-flex items-center gap-1 text-sm mt-2 block">
                        Læs mere om generaliseret angst her <ArrowRight size={14} />
                      </Link>
                    </>
                  )
                },
                {
                  title: "Social angst",
                  content: (
                    <>
                      Social angst handler om en intens frygt for at blive bedømt, kritiseret eller kigget på af andre i sociale situationer. Det fører ofte til, at man trækker sig fra sociale arrangementer eller føler sig ekstremt utilpas i andres selskab.{" "}
                      <Link to="/social-angst" className="text-[#8C2F12] font-semibold hover:underline inline-flex items-center gap-1 text-sm mt-2 block">
                        Læs mere om social angst her <ArrowRight size={14} />
                      </Link>
                    </>
                  )
                },
                {
                  title: "Helbredsangst",
                  content: (
                    <>
                      Helbredsangst er en overdreven bekymring for at fejle noget alvorligt eller livstruende. Man tjekker ofte sin krop for symptomer og søger gentagne gange beroligelse, men frygten for sygdom vender hurtigt tilbage.{" "}
                      <Link to="/helbredsangst" className="text-[#8C2F12] font-semibold hover:underline inline-flex items-center gap-1 text-sm mt-2 block">
                        Læs mere om helbredsangst her <ArrowRight size={14} />
                      </Link>
                    </>
                  )
                },
                {
                  title: "Fobier",
                  content: "En fobi er en intens og irrationel frygt for specifikke ting eller situationer, såsom edderkopper, højder, flyvning eller nåle. Selvom man rationelt ved, at faren er begrænset, reagerer kroppen med kraftig angst."
                },
                {
                  title: "Præstationsangst",
                  content: "Præstationsangst opstår i situationer, hvor man skal præstere noget foran andre, f.eks. til eksamen, jobsamtaler eller ved fremlæggelser. Frygten for ikke at slå til eller at fejle kan være så lammende, at det påvirker ens evne til at udføre opgaven."
                },
                {
                  title: "PTSD/traumerelateret angst",
                  content: (
                    <>
                      PTSD kan opstå efter voldsomme eller traumatiske oplevelser. Det viser sig ofte ved flashbacks, mareridt, øget vagtsomhed og en tendens til at undgå ting eller steder, der minder om traumet.{" "}
                      <Link to="/traume" className="text-[#8C2F12] font-semibold hover:underline inline-flex items-center gap-1 text-sm mt-2 block">
                        Læs mere om traumer og PTSD her <ArrowRight size={14} />
                      </Link>
                    </>
                  )
                }
              ]} />
            </div>
          </div>

          {/* Symptoms Section - MOVED UP */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 py-20 border-t border-[#E8DDCE]/60">
            <div>
              <h2 className="text-3xl font-serif text-[#123F13] mb-6">Typiske symptomer på angst</h2>
              <ul className="space-y-3">
                {[
                  "Hjertebanken og indre uro",
                  "Svimmelhed og trykken for brystet",
                  "Muskelspændinger og rastløshed",
                  "Tankemylder og bekymringstanker",
                  "Undgåelsesadfærd i sociale eller pressede situationer",
                  "Søvnbesvær og træthed",
                  "Maveproblemer og kvalme",
                  "Frygt for at miste kontrollen eller blive “skør”"
                ].map((symptom, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#3B352F]/70">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#8C2F12]" />
                    {symptom}
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-6 bg-[#FAF7F2] rounded-xl border border-[#E8DDCE] space-y-4">
                <div>
                  <p className="text-[#123F13] font-medium mb-2">Oplever du også søvnbesvær eller træthed i forbindelse med din angst?</p>
                  <Link to="/soevnproblemer" className="text-[#8C2F12] font-bold hover:underline inline-flex items-center gap-2 text-sm">
                    Læs om behandling af søvnproblemer <ArrowRight size={16} />
                  </Link>
                </div>
                <div className="pt-4 border-t border-[#E8DDCE]">
                  <p className="text-[#123F13] font-medium mb-2">Skyldes din angst sanseoverbelastning eller autistiske træk?</p>
                  <Link to="/autismevenlig-terapi" className="text-[#8C2F12] font-bold hover:underline inline-flex items-center gap-2 text-sm">
                    Læs om mit autismevenlige forløb <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-xl border border-[#E8DDCE] shadow-sm">
              <h3 className="text-xl font-bold text-[#123F13] mb-4">Hvorfor min behandling mod angst virker</h3>
              <p className="text-[#3B352F]/70 mb-6">
                Hos Core Hypnose handler det ikke om at analysere din barndom eller grave i gamle traumer. I stedet fokuserer jeg på det, du mærker og tænker lige nu, og hvordan du kan håndtere det mere hensigtsmæssigt.
              </p>
              <p className="text-[#3B352F]/70 mb-6">
                Du får værktøjer, som virker i hverdagen. Enkle, evidensbaserede teknikker, der hjælper dig med at skabe afstand til tankemylder og genvinde ro og kontrol.
              </p>
              <p className="text-[#3B352F]/70 italic">
                Du behøver ikke forstå “alt” for at få det bedre, du skal bare mærke, at du kan få det bedre. Og det begynder ofte allerede i første session.
              </p>
            </div>
          </div>
        </div>
      </section>

        {/* Sektion: Hvorfor bliver angsten ved & Når løsningen bliver en del af problemet */}
        <section className="bg-[#FAF7F2] p-8 md:p-12 rounded-2xl border border-[#E8DDCE] space-y-12 my-20">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-serif text-[#123F13] mb-6">Hvorfor bliver angsten ved at komme?</h2>
            <div className="space-y-4 text-[#3B352F]/70 leading-relaxed text-lg">
              <p>
                Angst føles ofte som noget, der kommer ud af det blå, men det, der fastholder den, er måden, du reagerer på den. Når du forsøger at få styr på dine tanker, tjekker kroppen, analyserer situationer eller undgår bestemte ting, så føles det som midlertidig lettelse. Men på længere sigt holder det angsten ved lige.
              </p>
              <p>
                Behandlingen hos Core Hypnose handler ikke om at “fjerne angst”, men om at skabe et nyt forhold til den, hvor du får kontrol, ro og handlefrihed tilbage i livet.
              </p>
              <p className="text-base border-l-2 border-[#8C2F12]/30 pl-4 py-1">
                Nogle oplever også, at angst, uro eller bekymring hænger tæt sammen med relationelle mønstre og dynamikker i parforholdet. Her kan <Link to="/parterapi" className="text-[#8C2F12] font-semibold hover:underline">parterapi eller relationscoaching</Link> være en værdifuld hjælp.
              </p>
            </div>
          </div>

        {/* Når løsningen bliver en del af problemet */}
        <div className="pt-12 border-t border-[#E8DDCE]/60">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <span className="text-[#8C2F12] text-xs font-semibold tracking-[0.15em] uppercase mb-4 block">Forstå mønstret</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#123F13] mb-4">
                Når løsningen bliver en del af problemet
              </h2>
              <p className="text-[#3B352F]/80 text-lg max-w-3xl mx-auto leading-relaxed">
                Det er ikke dig, der gør noget forkert. Ofte er det de strategier, du bruger for at få ro, som kan komme til at holde angsten i gang.
              </p>
            </div>

            {/* 7 Steps Process Flow */}
            <div className="mb-16 lg:mb-24">
              {/* Desktop Layout (hidden on mobile, visible on lg screens) */}
              <div className="hidden lg:block relative pb-12">
                <div className="flex lg:flex-row items-stretch lg:items-center justify-between gap-3 xl:gap-4">
                  {/* Step 1 */}
                  <div className="flex-1 flex flex-col items-center justify-between bg-white p-5 pt-8 pb-6 border border-[#E8DDCE]/60 shadow-xs transition-all hover:translate-y-[-2px] duration-200 rounded-t-[5rem] rounded-b-2xl min-h-[240px] w-full lg:w-auto">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#123F13] text-white text-sm font-semibold mb-4">
                      1
                    </span>
                    <div className="h-16 flex items-center justify-center text-[#123F13] mb-4">
                      <Activity size={36} strokeWidth={1.5} />
                    </div>
                    <p className="text-center text-xs md:text-sm font-sans text-[#3B352F] font-medium leading-snug">
                      Du mærker uro
                    </p>
                  </div>

                  {/* Step 2 */}
                  <div className="flex-1 flex flex-col items-center justify-between bg-white p-5 pt-8 pb-6 border border-[#E8DDCE]/60 shadow-xs transition-all hover:translate-y-[-2px] duration-200 rounded-t-[5rem] rounded-b-2xl min-h-[240px] w-full lg:w-auto">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#123F13] text-white text-sm font-semibold mb-4">
                      2
                    </span>
                    <div className="h-16 flex items-center justify-center text-[#123F13] mb-4">
                      <Search size={36} strokeWidth={1.5} />
                    </div>
                    <p className="text-center text-xs md:text-sm font-sans text-[#3B352F] font-medium leading-snug">
                      Du scanner kroppen
                    </p>
                  </div>

                  {/* Step 3 */}
                  <div className="flex-1 flex flex-col items-center justify-between bg-white p-5 pt-8 pb-6 border border-[#E8DDCE]/60 shadow-xs transition-all hover:translate-y-[-2px] duration-200 rounded-t-[5rem] rounded-b-2xl min-h-[240px] w-full lg:w-auto">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#123F13] text-white text-sm font-semibold mb-4">
                      3
                    </span>
                    <div className="h-16 flex items-center justify-center text-[#123F13] mb-4">
                      <Brain size={36} strokeWidth={1.5} />
                    </div>
                    <p className="text-center text-xs md:text-sm font-sans text-[#3B352F] font-medium leading-snug">
                      Du analyserer tanken
                    </p>
                  </div>

                  {/* Step 4 */}
                  <div className="flex-1 flex flex-col items-center justify-between bg-white p-5 pt-8 pb-6 border border-[#E8DDCE]/60 shadow-xs transition-all hover:translate-y-[-2px] duration-200 rounded-t-[5rem] rounded-b-2xl min-h-[240px] w-full lg:w-auto">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#123F13] text-white text-sm font-semibold mb-4">
                      4
                    </span>
                    <div className="h-16 flex items-center justify-center text-[#123F13] mb-4">
                      <Shield size={36} strokeWidth={1.5} />
                    </div>
                    <p className="text-center text-xs md:text-sm font-sans text-[#3B352F] font-medium leading-snug">
                      Du søger sikkerhed
                    </p>
                  </div>

                  {/* Step 5 */}
                  <div className="flex-1 flex flex-col items-center justify-between bg-white p-5 pt-8 pb-6 border border-[#E8DDCE]/60 shadow-xs transition-all hover:translate-y-[-2px] duration-200 rounded-t-[5rem] rounded-b-2xl min-h-[240px] w-full lg:w-auto">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#123F13] text-white text-sm font-semibold mb-4">
                      5
                    </span>
                    <div className="h-16 flex items-center justify-center text-[#123F13] mb-4">
                      <EyeOff size={36} strokeWidth={1.5} />
                    </div>
                    <p className="text-center text-xs md:text-sm font-sans text-[#3B352F] font-medium leading-snug">
                      Du undgår situationen
                    </p>
                  </div>

                  {/* Step 6 */}
                  <div className="flex-1 flex flex-col items-center justify-between bg-white p-5 pt-8 pb-6 border border-[#E8DDCE]/60 shadow-xs transition-all hover:translate-y-[-2px] duration-200 rounded-t-[5rem] rounded-b-2xl min-h-[240px] w-full lg:w-auto">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#123F13] text-white text-sm font-semibold mb-4">
                      6
                    </span>
                    <div className="h-16 flex items-center justify-center text-[#123F13] mb-4">
                      <Smile size={36} strokeWidth={1.5} />
                    </div>
                    <p className="text-center text-xs md:text-sm font-sans text-[#3B352F] font-medium leading-snug">
                      Du får kortvarig ro
                    </p>
                  </div>

                  {/* Step 7 - Highlighted Step Card */}
                  <div className="flex-1 flex flex-col items-center justify-between bg-[#FDF5F2] p-5 pt-8 pb-6 border-2 border-[#8C2F12] shadow-sm rounded-2xl min-h-[240px] w-full lg:w-auto">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#8C2F12] text-white text-sm font-semibold mb-4">
                      7
                    </span>
                    <div className="h-16 flex items-center justify-center text-[#8C2F12] mb-4">
                      <div className="relative">
                        <Brain size={36} strokeWidth={1.5} />
                        <Lock size={14} className="absolute -bottom-1 -right-1 bg-[#FDF5F2] rounded-full p-0.5 text-[#8C2F12]" />
                      </div>
                    </div>
                    <p className="text-center text-xs md:text-sm font-semibold text-[#123F13] leading-snug">
                      Hjernen lærer:<br />
                      <span className="italic font-serif text-[#8C2F12]">"Det var farligt"</span>
                    </p>
                  </div>
                </div>

                {/* Desktop Return Loop Arrow */}
                <svg aria-hidden="true" className="absolute left-0 right-0 bottom-[-40px] h-16 w-full hidden lg:block text-[#8C2F12]" viewBox="0 0 1000 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Curved path */}
                  <path
                    d="M 928,0 C 928,45 72,45 72,12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeDasharray="6,4"
                    strokeLinecap="round"
                  />
                  {/* Arrowhead pointing up at Step 1 */}
                  <path
                    d="M 67,18 L 72,8 L 77,18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                {/* Desktop Return Loop Badge */}
                <div className="absolute left-1/2 bottom-[-48px] -translate-x-1/2 bg-[#FDF5F2] border border-[#8C2F12]/30 px-3 py-1 rounded-full text-[11px] font-medium text-[#8C2F12] tracking-wider uppercase hidden lg:flex items-center gap-1.5 shadow-xs">
                  <RefreshCw size={10} className="animate-spin-slow" />
                  Mønsteret gentages
                </div>
              </div>

              {/* Mobile Layout (visible on mobile, hidden on lg screens) */}
              <div className="lg:hidden relative pl-2 pr-2">
                {/* Vertical Connecting Line */}
                <div className="absolute left-[27px] top-6 bottom-6 w-[2px] border-l-2 border-dashed border-[#8C2F12]/30 z-0" />

                <div className="space-y-4 relative z-10">
                  {/* Step 1 */}
                  <div className="flex items-start gap-4 bg-white p-4 rounded-xl border border-[#E8DDCE]/60 shadow-xs">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#123F13] text-white flex items-center justify-center font-bold text-sm">
                      1
                    </div>
                    <div className="flex items-center gap-3 py-1">
                      <div className="text-[#123F13] bg-[#E2ECE3]/40 p-1.5 rounded-lg">
                        <Activity size={20} strokeWidth={1.5} />
                      </div>
                      <p className="font-sans text-[#3B352F] font-medium text-sm sm:text-base">
                        Du mærker uro
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-4 bg-white p-4 rounded-xl border border-[#E8DDCE]/60 shadow-xs">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#123F13] text-white flex items-center justify-center font-bold text-sm">
                      2
                    </div>
                    <div className="flex items-center gap-3 py-1">
                      <div className="text-[#123F13] bg-[#E2ECE3]/40 p-1.5 rounded-lg">
                        <Search size={20} strokeWidth={1.5} />
                      </div>
                      <p className="font-sans text-[#3B352F] font-medium text-sm sm:text-base">
                        Du scanner kroppen
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-4 bg-white p-4 rounded-xl border border-[#E8DDCE]/60 shadow-xs">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#123F13] text-white flex items-center justify-center font-bold text-sm">
                      3
                    </div>
                    <div className="flex items-center gap-3 py-1">
                      <div className="text-[#123F13] bg-[#E2ECE3]/40 p-1.5 rounded-lg">
                        <Brain size={20} strokeWidth={1.5} />
                      </div>
                      <p className="font-sans text-[#3B352F] font-medium text-sm sm:text-base">
                        Du analyserer tanken
                      </p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex items-start gap-4 bg-white p-4 rounded-xl border border-[#E8DDCE]/60 shadow-xs">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#123F13] text-white flex items-center justify-center font-bold text-sm">
                      4
                    </div>
                    <div className="flex items-center gap-3 py-1">
                      <div className="text-[#123F13] bg-[#E2ECE3]/40 p-1.5 rounded-lg">
                        <Shield size={20} strokeWidth={1.5} />
                      </div>
                      <p className="font-sans text-[#3B352F] font-medium text-sm sm:text-base">
                        Du søger sikkerhed
                      </p>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div className="flex items-start gap-4 bg-white p-4 rounded-xl border border-[#E8DDCE]/60 shadow-xs">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#123F13] text-white flex items-center justify-center font-bold text-sm">
                      5
                    </div>
                    <div className="flex items-center gap-3 py-1">
                      <div className="text-[#123F13] bg-[#E2ECE3]/40 p-1.5 rounded-lg">
                        <EyeOff size={20} strokeWidth={1.5} />
                      </div>
                      <p className="font-sans text-[#3B352F] font-medium text-sm sm:text-base">
                        Du undgår situationen
                      </p>
                    </div>
                  </div>

                  {/* Step 6 */}
                  <div className="flex items-start gap-4 bg-white p-4 rounded-xl border border-[#E8DDCE]/60 shadow-xs">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#123F13] text-white flex items-center justify-center font-bold text-sm">
                      6
                    </div>
                    <div className="flex items-center gap-3 py-1">
                      <div className="text-[#123F13] bg-[#E2ECE3]/40 p-1.5 rounded-lg">
                        <Smile size={20} strokeWidth={1.5} />
                      </div>
                      <p className="font-sans text-[#3B352F] font-medium text-sm sm:text-base">
                        Du får kortvarig ro
                      </p>
                    </div>
                  </div>

                  {/* Step 7 */}
                  <div className="flex items-start gap-4 bg-[#FDF5F2] p-4 rounded-xl border-2 border-[#8C2F12] shadow-sm">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#8C2F12] text-white flex items-center justify-center font-bold text-sm">
                      7
                    </div>
                    <div className="flex items-center gap-3 py-1">
                      <div className="text-[#8C2F12] bg-[#FDF0EC] p-1.5 rounded-lg relative">
                        <Brain size={20} strokeWidth={1.5} />
                        <Lock size={10} className="absolute -bottom-0.5 -right-0.5 bg-[#FDF5F2] rounded-full p-0.2 text-[#8C2F12]" />
                      </div>
                      <div>
                        <p className="font-sans text-[#123F13] font-semibold text-sm sm:text-base">
                          Hjernen lærer: <span className="italic font-serif text-[#8C2F12]">"Det var farligt"</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mobile Return Loop Indicator */}
                <div className="mt-6 bg-[#FDF5F2] border border-[#8C2F12]/30 rounded-xl p-4 flex items-center justify-between gap-3 shadow-xs">
                  <div className="flex items-center gap-2.5 text-[#8C2F12]">
                    <RefreshCw size={16} className="animate-spin-slow shrink-0" />
                    <span className="text-xs font-semibold uppercase tracking-wider">Mønsteret gentages</span>
                  </div>
                  <div className="flex items-center gap-1 text-[#8C2F12]/70 text-xs font-medium">
                    <span>Tilbage til trin 1</span>
                    <ArrowUp size={14} className="animate-bounce" />
                  </div>
                </div>
              </div>
            </div>

            {/* Mønstret bag lettelsen - Full width elegant card */}
            <div className="bg-white border border-[#E8DDCE]/60 rounded-3xl p-6 md:p-10 shadow-sm max-w-4xl mx-auto mt-8">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6 text-center md:text-left">
                <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[#FAF7F2] flex items-center justify-center text-[#123F13] mx-auto md:mx-0 shadow-xs">
                  <Leaf size={28} />
                </div>
                <div>
                  <h3 className="font-serif text-2xl text-[#123F13] mb-3 font-semibold">
                    Mønstret bag lettelsen
                  </h3>
                  <p className="text-[#3B352F]/80 text-base md:text-lg leading-relaxed">
                    Det, der giver lettelse på kort sigt, kan nogle gange holde angsten i live på længere sigt. Derfor handler behandlingen om at ændre mønsteret — ikke om at bebrejde dig for det.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Buttons inside section */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8 px-4 sm:px-0">
          <Link to="/bestil-tid" className="inline-flex items-center justify-center gap-2 bg-[#193f10] text-white px-6 py-3.5 rounded-md font-bold text-base hover:bg-[#193f10]/90 transition-all shadow-md w-full sm:w-auto">
            <Calendar size={18} /> Book første behandlingssession
          </Link>
          <a href="tel:27204671" className="inline-flex items-center justify-center gap-2 border-2 border-[#193f10] text-[#193f10] px-6 py-3.5 rounded-md font-bold text-base hover:bg-[#faf7f2] transition-all w-full sm:w-auto">
            <Phone size={18} /> Tlf. 27 20 46 71
          </a>
        </div>
      </section>

      {/* Hvad behandlingen fokuserer på — og ikke fokuserer på */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="font-sans text-xs tracking-[0.2em] uppercase text-[#8C2F12] font-semibold block mb-3">
              Behandlingsfokus
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-4">
              Hvad behandlingen fokuserer på — og ikke fokuserer på
            </h2>
            <p className="text-[#3B352F]/80 text-base md:text-lg leading-relaxed">
              Målet er ikke at fjerne alle tanker eller gøre dig perfekt rolig. Målet er at hjælpe dig til at forstå angsten bedre og få mere frihed i hverdagen.
            </p>
          </div>

          {/* Comparison Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Card 1: Ikke fokus på */}
            <div className="bg-white border border-[#E8DDCE]/60 rounded-3xl p-6 md:p-8 flex flex-col justify-between shadow-sm">
              <div>
                <h3 className="font-serif text-2xl text-[#8C2F12] text-center mb-6">
                  Ikke fokus på
                </h3>
                <ul className="divide-y divide-[#E8DDCE]/40">
                  {[
                    "At fjerne alle tanker",
                    "At analysere alt fra fortiden",
                    "At kontrollere kroppen perfekt",
                    "At undgå alt, der føles ubehageligt"
                  ].map((item, idx) => (
                    <li key={idx} className="py-4 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#FDF5F2] flex items-center justify-center shrink-0 text-[#8C2F12]">
                          <X size={18} />
                        </div>
                        <span className="font-sans text-sm md:text-base text-[#3B352F]/90">
                          {item}
                        </span>
                      </div>
                      <span className="w-6 h-6 rounded-full bg-[#FDF5F2] text-[#8C2F12] flex items-center justify-center text-xs shrink-0 font-bold">
                        ✕
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-8 text-center bg-[#FDF5F2] rounded-full py-3 px-6">
                <p className="font-serif italic text-sm text-[#8C2F12]">
                  Det skaber ofte mere kamp
                </p>
              </div>
            </div>

            {/* Card 2: Fokus på */}
            <div className="bg-white border border-[#E8DDCE]/60 rounded-3xl p-6 md:p-8 flex flex-col justify-between shadow-sm">
              <div>
                <h3 className="font-serif text-2xl text-[#123F13] text-center mb-6">
                  Fokus på
                </h3>
                <ul className="divide-y divide-[#E8DDCE]/40">
                  {[
                    "At forstå alarmreaktionen",
                    "At ændre forholdet til tanker",
                    "At mindske overvågning og undgåelse",
                    "At skabe frihed i hverdagen"
                  ].map((item, idx) => (
                    <li key={idx} className="py-4 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#FAF7F2] flex items-center justify-center shrink-0 text-[#123F13]">
                          <Check size={18} />
                        </div>
                        <span className="font-sans text-sm md:text-base text-[#3B352F]/90">
                          {item}
                        </span>
                      </div>
                      <span aria-hidden="true" className="w-6 h-6 rounded-full bg-[#123F13] text-white flex items-center justify-center text-xs shrink-0 font-bold">
                        ✓
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-8 text-center bg-[#E2E8E3] rounded-full py-3 px-6">
                <p className="font-serif italic text-sm text-[#123F13]">
                  Det skaber mere ro og retning
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Section: Et nyt forhold til dine tanker & Derfor giver det mening */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            {/* Et nyt forhold til dine tanker */}
            <div className="lg:col-span-6 bg-[#FAF7F2] border border-[#E8DDCE]/60 rounded-3xl p-6 md:p-8 flex flex-col justify-center shadow-sm">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white flex items-center justify-center text-[#123F13] mb-2 sm:mb-0 mx-auto sm:mx-0 shadow-xs">
                  <Leaf size={24} />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-[#123F13] mb-2 font-semibold">Et nyt forhold til dine tanker</h3>
                  <p className="text-[#3B352F]/80 text-base leading-relaxed">
                    Behandlingen handler ikke om at love et liv uden tanker eller ubehag. Den handler om at give dig en ny måde at forholde dig til dem på.
                  </p>
                </div>
              </div>
            </div>

            {/* Derfor giver det mening */}
            <div className="lg:col-span-6 bg-white border border-[#E8DDCE]/60 rounded-3xl p-6 md:p-8 flex flex-col justify-center shadow-sm">
              <h3 className="font-serif text-xl text-[#123F13] mb-5 font-semibold text-center sm:text-left">
                Derfor giver det mening:
              </h3>
              <div className="space-y-3.5">
                {[
                  { text: "Mindre selvovervågning", desc: "Du slipper kontrollen af kroppen" },
                  { text: "Mere indre ro", desc: "Tankerne får lov at falde til ro" },
                  { text: "Større frihed i hverdagen", desc: "Du gør det, du har lyst til" }
                ].map((item, idx) => (
                  <div key={idx} className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40 flex items-center gap-4 transition-all hover:bg-[#FAF7F2]/80">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#123F13] shadow-xs">
                      <span className="text-xs font-bold font-serif">0{idx + 1}</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-serif text-sm font-bold text-[#123F13]">
                        {item.text}
                      </h4>
                      <p className="text-xs text-[#3B352F]/70 mt-0.5">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

        {/* Sådan foregår det sektion */}
        <section className="py-20 border-t border-[#E8DDCE]/60">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="font-sans text-xs tracking-[0.2em] uppercase text-[#8C2F12] font-semibold block mb-3">
              Behandlingsforløb
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13]">
              Sådan foregår angstbehandling med hypnose & metakognitiv terapi
            </h2>
          </div>

          <div className="max-w-4xl mx-auto relative pl-6 md:pl-10">
            {/* Vertical timeline line */}
            <div className="absolute left-[27px] md:left-[37px] top-4 bottom-4 w-[2px] bg-[#E8DDCE]/60" />

            <div className="space-y-12 relative z-10">
              {[
                { 
                  title: "Forstå dine reaktioner", 
                  content: "Jeg starter med at afmystificere angsten, så du forstår, hvad der sker i krop og sind." 
                },
                { 
                  title: "Metakognitive teknikker", 
                  content: "Du lærer at håndtere overfokusering og bekymringer, så tankerne mister grebet." 
                },
                { 
                  title: "Hypnose", 
                  content: "Jeg bruger hypnose til at skabe ro, kontakt til dit indre og styrke dine mentale ressourcer." 
                },
                { 
                  title: "Hjemmeopgaver og redskaber", 
                  content: "Du får enkle, effektive opgaver, som hjælper dig med at fastholde ro og forandring." 
                },
                { 
                  title: "Hold fast i forandringen", 
                  content: "Jeg arbejder med, hvordan du bevarer din ro og indre styrke – også når hverdagen presser på. Du får redskaber til at forebygge tilbagefald og fastholde det, du har opnået." 
                }
              ].map((step, i) => (
                <div key={i} className="flex gap-6 md:gap-8 items-start group">
                  {/* Number Badge on Timeline */}
                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-[#123F13] text-white rounded-full flex items-center justify-center font-bold text-base md:text-lg shadow-sm group-hover:scale-105 transition-transform duration-200">
                    {i + 1}
                  </div>
                  {/* Step Card */}
                  <div className="flex-grow bg-white border border-[#E8DDCE]/50 rounded-2xl p-6 md:p-8 shadow-xs hover:shadow-sm hover:border-[#E8DDCE] transition-all duration-300">
                    <h3 className="text-xl font-bold text-[#123F13] mb-3 font-serif">
                      {step.title}
                    </h3>
                    <p className="text-[#3B352F]/70 text-base md:text-lg leading-relaxed">
                      {step.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <div className="py-20 border-t border-[#E8DDCE]/60">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">FAQ - svar på dine spørgsmål</h2>
            <p className="text-[#3B352F]/70">
              Her finder du svar på nogle af de mest almindelige spørgsmål omkring angstbehandling hos Core Hypnose.
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <FAQAccordion items={[
              {
                question: "Hvor mange sessioner har jeg brug for?",
                answer: "Det varierer fra person til person, men mange mærker en betydelig forskel allerede efter 3-5 sessioner. Nogle har brug for færre, andre for et længere forløb afhængig af angstens karakter og varighed."
              },
              {
                question: "Hvordan foregår en behandling?",
                answer: "Jeg kombinerer samtale, metakognitive teknikker og hypnose. Jeg starter med at tale om dine udfordringer, hvorefter jeg arbejder med konkrete redskaber og afslutter ofte med en beroligende hypnose-session."
              },
              {
                question: "Kan jeg bruge behandlingen, hvis jeg har panikangst eller generaliseret angst?",
                answer: "Ja, absolut. Metoderne er yderst effektive over for både specifikke angstformer som panikangst og mere diffuse former som generaliseret angst."
              },
              {
                question: "Hvad koster det?",
                answer: "Du kan finde de aktuelle priser på min prisside. Jeg tilbyder også introtilbud til nye klienter."
              },
              {
                question: "Kan jeg få behandling online?",
                answer: "Ja, jeg tilbyder fuldt ud effektive online-sessioner via en sikker forbindelse, så du kan modtage behandling i trygge rammer hjemmefra."
              },
              {
                question: "Er hypnose farligt?",
                answer: "Nej, hypnose er en helt naturlig tilstand af fokuseret opmærksomhed, som minder om at dagdrømme. Du er altid i kontrol og kan ikke bringes til at gøre noget mod din vilje."
              }
            ]} />
          </div>
        </div>

        {/* Related Section */}
        <section className="py-16 border-t border-[#E8DDCE]/60">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-3xl mb-12">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Måske er dette også relevant for dig</span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
                Læs videre i det, der passer til din situation
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link to="/helbredsangst" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
                <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Helbredsangst og frygt for sygdom</h3>
                <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
              </Link>
              <Link to="/panikangst" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
                <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Panikangst og anfald</h3>
                <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
              </Link>
              <Link to="/social-angst" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
                <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Social angst og selvovervågning</h3>
                <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
              </Link>
              <Link to="/ocd" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
                <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">OCD og tvangstanker</h3>
                <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
              </Link>
            </div>
          </div>
        </section>

        {/* Outbound Links Section */}
        <div className="py-20 border-t border-[#E8DDCE]/60">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-serif text-[#123F13] mb-4">
              Vil du læse mere om angst fra neutrale kilder?
            </h2>
            <p className="text-[#3B352F]/70 text-sm md:text-base">
              Her finder du troværdige og uafhængige organisationer, hvor du kan læse mere om angst, få gode råd og finde støtte.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto px-4">
            {/* Card 1: Sundhed.dk */}
            <div className="flex flex-col items-center text-center bg-white border border-[#E8DDCE]/60 rounded-2xl p-6 shadow-xs transition-all hover:shadow-md duration-200">
              <div className="w-12 h-12 rounded-full bg-[#FAF7F2] flex items-center justify-center text-[#123F13] mb-4">
                <Activity size={22} />
              </div>
              <h3 className="text-lg font-bold text-[#123F13] mb-2">
                Sundhed.dk – Angst
              </h3>
              <p className="text-sm text-[#3B352F]/80 leading-relaxed mb-6 flex-grow">
                Få lægelig viden om symptomer, årsager, undersøgelser og behandlingsmuligheder.
              </p>
              <a
                href="https://www.sundhed.dk/borger/patienthaandbogen/psyke/symptomer/angst/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#8C2F12] hover:underline mt-auto"
              >
                Læs mere på sundhed.dk <ExternalLink size={14} />
              </a>
            </div>

            {/* Card 2: Psykiatrifonden */}
            <div className="flex flex-col items-center text-center bg-white border border-[#E8DDCE]/60 rounded-2xl p-6 shadow-xs transition-all hover:shadow-md duration-200">
              <div className="w-12 h-12 rounded-full bg-[#FAF7F2] flex items-center justify-center text-[#123F13] mb-4">
                <Brain size={22} />
              </div>
              <h3 className="text-lg font-bold text-[#123F13] mb-2">
                Psykiatrifonden – Angst
              </h3>
              <p className="text-sm text-[#3B352F]/80 leading-relaxed mb-6 flex-grow">
                Viden om angst, behandling og praktiske råd til dig og dine pårørende.
              </p>
              <a
                href="https://psykiatrifonden.dk/diagnoser/angst"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#8C2F12] hover:underline mt-auto"
              >
                Læs mere på psykiatrifonden.dk <ExternalLink size={14} />
              </a>
            </div>

            {/* Card 3: Angst & Stressforeningen */}
            <div className="flex flex-col items-center text-center bg-white border border-[#E8DDCE]/60 rounded-2xl p-6 shadow-xs transition-all hover:shadow-md duration-200">
              <div className="w-12 h-12 rounded-full bg-[#FAF7F2] flex items-center justify-center text-[#123F13] mb-4">
                <Shield size={22} />
              </div>
              <h3 className="text-lg font-bold text-[#123F13] mb-2">
                Angst & Stressforeningen
              </h3>
              <p className="text-sm text-[#3B352F]/80 leading-relaxed mb-6 flex-grow">
                Støtte, viden og fællesskab for dig, der lever med angst, stress eller OCD.
              </p>
              <a
                href="https://www.angststress.dk/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#8C2F12] hover:underline mt-auto"
              >
                Læs mere på angststress.dk <ExternalLink size={14} />
              </a>
            </div>

            {/* Card 4: Internetpsykiatrien */}
            <div className="flex flex-col items-center text-center bg-white border border-[#E8DDCE]/60 rounded-2xl p-6 shadow-xs transition-all hover:shadow-md duration-200">
              <div className="w-12 h-12 rounded-full bg-[#FAF7F2] flex items-center justify-center text-[#123F13] mb-4">
                <Smile size={22} />
              </div>
              <h3 className="text-lg font-bold text-[#123F13] mb-2">
                Internetpsykiatrien
              </h3>
              <p className="text-sm text-[#3B352F]/80 leading-relaxed mb-6 flex-grow">
                Gratis online behandling for voksne med let til moderat angst eller depression.
              </p>
              <a
                href="https://internetpsykiatrien.dk/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#8C2F12] hover:underline mt-auto"
              >
                Læs mere på internetpsykiatrien.dk <ExternalLink size={14} />
              </a>
            </div>
          </div>

          <div className="mt-8 text-center max-w-2xl mx-auto px-4">
            <p className="text-xs text-[#3B352F]/60 leading-relaxed">
              Links fører til eksterne, uafhængige hjemmesider. Core Hypnose har ingen økonomisk interesse i disse organisationer.
            </p>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center py-20 border-t border-[#E8DDCE]/60 bg-[#FAF7F2]/30 rounded-3xl px-6">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-4">Klar til at få ro og kontrol igen?</h2>
          <p className="text-[#3B352F]/70 text-base md:text-lg max-w-2xl mx-auto mb-8">
            Tag det første skridt mod en mere fri og rolig hverdag. Du er altid velkommen til at kontakte mig, hvis du har spørgsmål.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 max-w-md mx-auto">
            <Link to="/bestil-tid" className="inline-flex items-center justify-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#a33a1a] transition-all shadow-lg w-full sm:w-auto">
              Book din første session nu <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </ServiceLayout>
  );
};

export default Angst;
